<template>
  <div class="group relative w-full">
    <div
      class="absolute left-5 top-1/4 z-10 flex flex-col gap-2 opacity-0 transition-opacity group-hover:opacity-100"
      @click.stop
    >
      <UiTooltip
        :id="`add-to-wishlist-${product.id}`"
        :label="authStore.isLoggedIn ? $t('product.addToWishlist') : $t('wishlist.loginRequired')"
      >
        <UiIconButton
          :id="`add-to-wishlist-${product.id}`"
          variant="secondary"
          type="filled"
          :icon="IconHeart"
          :disabled="!authStore.isLoggedIn || !!isLoading || isInWishlist"
          :is-loading="isLoading === 'wishlist'"
          rounded
          @click="addToWishlist()"
        />
      </UiTooltip>
      <UiTooltip :id="`add-to-cart-${product.id}`" :label="$t('product.addToCart')">
        <UiIconButton
          :id="`add-to-cart-${product.id}`"
          variant="secondary"
          type="filled"
          :icon="IconCart"
          :disabled="!!isLoading"
          :is-loading="isLoading === 'cart'"
          rounded
          @click="addToCart()"
        />
      </UiTooltip>
    </div>

    <UiLink :id="`product-list-card-${product.id}`" :to="productUrl" :underline="false" @click="clickAction()">
      <div
        class="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl bg-neutral-100 object-cover"
      >
        <UiImage
          v-if="product.images?.[0]"
          :id="`product-list-card-${product.id}`"
          class="size-full rounded-2xl"
          :src="product.images[0]"
          :alt="product.name"
        />
      </div>
      <UiText class="my-2 block text-black">{{ product.name }}</UiText>
      <ProductPrice :product="product" />
    </UiLink>
  </div>
</template>

<script setup lang="ts">
import { IconCart, IconHeart } from '#components';

import type { ProductListCardProps } from '~/components/Product/types';

import { useAlertStore } from '~/stores/alert/alertStore';
import { useAppStore } from '~/stores/app/appStore';
import { useAuthStore } from '~/stores/auth/authStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { CartActionType } from '~/stores/cart/types';
import { WishlistActionType } from '~/stores/wishlist/types';
import { useWishlistStore } from '~/stores/wishlist/wishlistStore';

const { locale } = useI18n();
const localePath = useLocalePath();
const alertStore = useAlertStore();
const route = useRoute();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const cartStore = useCartStore();
const appStore = useAppStore();
const wishlistStore = useWishlistStore();
const authStore = useAuthStore();

const isLoading = ref<'cart' | 'wishlist' | null>(null);

const props = defineProps<ProductListCardProps>();

const isInWishlist = computed(
  () => !!wishlistStore.getWishlistLineItems?.find((item) => item.sku === props.product?.sku),
);

const productUrl = computed(() =>
  localePath(
    `${route.params?.slug?.length ? `/${(route.params.slug as string[]).join('/')}` : ''}/${props.product.slug[locale.value as Locale]}`,
  ),
);

const addToCart = (): void => {
  if (!props.product) {
    return;
  }

  dataLayer.push({
    component: 'product_list_card',
    event: 'button_click',
    product: props.product.name,
    value: 'add_to_cart',
  });

  isLoading.value = 'cart';

  cartStore
    .updateCart({
      actions: [
        {
          quantity: 1,
          sku: props.product.sku,
          type: CartActionType.ADD_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'product.addedToCartSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = null));
};

const addToWishlist = (): void => {
  if (!props.product) {
    return;
  }

  dataLayer.push({
    component: 'product_list_card',
    event: 'button_click',
    product: props.product.name,
    value: 'add_to_wishlist',
  });

  isLoading.value = 'wishlist';

  wishlistStore
    .updateWishlist({
      actions: [
        {
          sku: props.product.sku,
          type: WishlistActionType.ADD_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'product.addedToWishlistSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = null));
};

const clickAction = (): void => {
  dataLayer.push({
    component: 'product_list_card',
    event: 'card_click',
    value: props.product.name,
  });
};
</script>
